// Sync transport — replays queued REST calls (url + method + payload stored at enqueue time)

import { fetchWithTimeout } from '../../../utils/fetch-with-timeout.js';
import { refreshCoordinator } from '../../auth/refresh-coordinator.js';
import { setToken } from '../../../services/storage/localStorage.service.js';

class SyncTransport {
  /**
   * Attempt to refresh the access token via the auth refresh endpoint.
   * Uses the same pattern as base.api.js refreshToken().
   */
  async refreshAccessToken(mutationUrl) {
    const ts = Date.now();
    const before = localStorage.getItem('accessToken');
    const beforePrefix = before ? before.substring(0, 10) : 'null';
    console.log(`[SYNCTRANSPORT-AUTH] ${ts} SYNCTRANSPORT_REFRESH_STARTED tokenBefore:${beforePrefix}`);

    const baseUrl = new URL(mutationUrl).origin;
    try {
      const accessToken = await refreshCoordinator.executeRefresh(`${baseUrl}/auth/refresh`);
      const newPrefix = accessToken ? accessToken.substring(0, 10) : 'null';
      setToken(accessToken);
      const after = localStorage.getItem('accessToken');
      const afterPrefix = after ? after.substring(0, 10) : 'null';
      console.log(`[SYNCTRANSPORT-AUTH] ${Date.now()} SYNCTRANSPORT_REFRESH_SUCCEEDED newTokenPrefix:${newPrefix} tokenAfterSet:${afterPrefix}`);
      return accessToken;
    } catch (error) {
      console.log(`[SYNCTRANSPORT-AUTH] ${Date.now()} SYNCTRANSPORT_REFRESH_FAILED status:${error.status}`);
      throw error;
    }
  }

  /**
   * Replay a single queued mutation against its original REST endpoint.
   */
  async executeMutation(mutation) {
    if (!mutation?.url || !mutation?.method) {
      throw new Error('Mutation missing url or method for replay');
    }

    const buildRequestInit = (token) => {
      const { Authorization: _storedAuth, ...safeStoredHeaders } = mutation.headers || {};
      const headers = {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...(mutation.idempotencyKey && {
          'X-Idempotency-Key': mutation.idempotencyKey,
        }),
        ...safeStoredHeaders,
      };
      const init = {
        method: mutation.method,
        headers,
        credentials: 'include',
      };
      if (
        ['POST', 'PUT', 'PATCH'].includes(mutation.method) &&
        mutation.payload != null
      ) {
        const { clientRequestId, _optimisticId, ...cleanPayload } = mutation.payload;
        init.body = JSON.stringify(cleanPayload);
      }
      return init;
    };

    const sendRequest = async (token) => {
      const init = buildRequestInit(token);
      console.log('[SyncTransport] Replaying:', mutation.method, mutation.url);
      const res = await fetchWithTimeout(mutation.url, init, 5000);
      console.log('[SyncTransport] Response:', res.status, res.statusText);
      return res;
    };

    // First attempt
    let token = localStorage.getItem('accessToken');
    let response = await sendRequest(token);

    // On 401, try refreshing the token and retry once
    if (response.status === 401) {
      console.log('[SyncTransport] Got 401, attempting token refresh');
      try {
        token = await this.refreshAccessToken(mutation.url);
        response = await sendRequest(token);
      } catch (refreshError) {
        console.error('[SyncTransport] Token refresh failed, propagating original 401');
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `Sync replay failed: ${response.status}`
        );
      }
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `Sync replay failed: ${response.status}`
      );
    }

    if (response.status === 204) {
      return { status: 'success', result: null };
    }

    const result = await response.json();
    if (result?.queued) {
      throw new Error('Sync replay was queued instead of executed');
    }

    return { status: 'success', result };
  }

  /**
   * @deprecated Batch sync endpoint — kept for compatibility; prefer executeMutation.
   */
  async executeBatch(mutations) {
    const results = [];
    for (const mutation of mutations) {
      results.push(await this.executeMutation(mutation));
    }
    return results;
  }
}

export const syncTransport = new SyncTransport();
